import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import { Section } from '../../layout/Section';

import { Slider } from '../Slider/Slider';

const RealWorldSlider = styled(Slider)`
  @media (max-width: 768px) {
    .--content-container .--content .--image .gatsby-image-wrapper {
      border-radius: 16px;
    }
  }
`;

const VerticalSlider = styled(RealWorldSlider)`
  .--content {
    display: flex !important;
    flex-direction: column !important;
    padding-top: 32px !important;
  }
  .--content .--text,
  .--content .--image {
    width: 100% !important;
    padding-bottom: 0 !important;
  }
  .--content .--text p {
    margin: 0px 0px 40px !important;
    padding: 0px !important;
    max-width: 604px;

    &:last-child {
      margin-bottom: 40px !important;
    }
  }
  .--content .--image .gatsby-image-wrapper {
    border-radius: 32px;
  }
`;

const StyledSection = styled<typeof Section>(Section)`
  overflow: clip;

  /* 150px top padding on desktop (Section base is 100px); triple-& outweighs
     the .twentyTwentyFour .section .content base selector. Bottom padding is
     the 150px on .showcase-content below. */
  &&& .content {
    padding-top: 150px;
  }

  /* Line break that only takes effect on mobile (use in heading content). */
  .mobile-br {
    display: none;
    @media (max-width: 768px) {
      display: inline;
    }
  }

  /* Opt-in variant: centre the heading and subheading on mobile. */
  &.--centered-mobile-header {
    @media (max-width: 768px) {
      h2 {
        text-align: center !important;
      }
      .--subheader {
        text-align: center !important;
      }
    }
  }

  h2 {
    margin: 0 0 32px 0 !important;
    @media (max-width: 768px) {
      margin-bottom: 20px !important;
      text-align: left !important;
    }
  }

  &.no-subheading h2 {
    margin-bottom: 80px !important;
    @media (max-width: 768px) {
      margin-bottom: 40px !important;
    }
  }

  @media (max-width: 768px) {
    padding: 60px 0 0 0;
    .content {
      /* Re-assert the global mobile gutter so this band never relies on
         page-level styles to keep its 25px side margins */
      margin: 0 25px;
      padding-top: 0 !important;
    }
  }

  .--subheader {
    grid-column: 4 / span 6;
    text-align: center;
    margin-bottom: 80px;
    @media (max-width: 768px) {
      grid-column: 1 / span 12;
      margin-bottom: 40px;
      text-align: left !important;
    }
  }

  .showcase-content {
    grid-column: span 12;
    padding-bottom: 150px;
    @media (max-width: 768px) {
      padding-bottom: 0px;
    }

    /* Styling for multiParagraphs - 12px margin between paragraphs */
    &:not(.vertical-stack) .--content .--text p {
      margin-top: 0 !important;
      margin-bottom: 12px !important;
    }

    &:not(.vertical-stack) .--content .--text p:last-child {
      margin-bottom: 0 !important;
    }
  }
  .grid-layout {
    margin-top: 0 !important;
    @media (max-width: 768px) {
      margin-top: 0 !important;
      padding-bottom: 70px;
    }
  }

  .--content .--image {
    display: flex;
    align-items: flex-end;
  }

  .--content .--image .gatsby-image-wrapper {
    width: 100%;
    aspect-ratio: 1 / 1;
  }

  .--content .--image .gatsby-image-wrapper img {
    object-fit: cover !important;
    width: 100% !important;
    height: 100% !important;
  }
`;

type BaseRealWorldSectionProps = {
  heading: React.ReactElement | string;
  subheading?: string;
  tabs: string[];
  headings: string[];
  whatYouWillLearn?: string[][];
  lessonBadges?: string[][];
  images: React.ReactNode[];
  stackVertical?: boolean;
  secondsToFlip?: number;
} & React.ComponentProps<typeof Section>;

type RealWorldSectionProps = BaseRealWorldSectionProps &
  (
    | { paragraphs: React.ReactNode[]; multiParagraphs?: never }
    | { paragraphs?: never; multiParagraphs: React.ReactNode[][] }
  );

export default function RealWorldSection({
  heading,
  subheading,
  tabs,
  headings,
  whatYouWillLearn,
  lessonBadges,
  images,
  stackVertical,
  secondsToFlip,
  className,
  ...restOfProps
}: RealWorldSectionProps & { className?: string }) {
  // Extract content props with proper type safety
  const paragraphs = 'paragraphs' in restOfProps ? restOfProps.paragraphs : undefined;
  const multiParagraphs =
    'multiParagraphs' in restOfProps ? restOfProps.multiParagraphs : undefined;

  // Filter out paragraphs and multiParagraphs from props to prevent passing to DOM
  const filteredProps = { ...restOfProps };
  delete (filteredProps as Record<string, unknown>).paragraphs;
  delete (filteredProps as Record<string, unknown>).multiParagraphs;

  // Build slider props based on which content type is provided
  const getSliderProps = () => {
    const baseProps = {
      tabs,
      headings,
      whatYouWillLearn,
      lessonBadges,
      images,
      secondsToFlip,
    };

    if (multiParagraphs) {
      return { ...baseProps, multiParagraphs };
    } else if (paragraphs) {
      return { ...baseProps, paragraphs };
    }

    return baseProps;
  };
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <StyledSection
      backgroundColor="--stile-green-2"
      headingColor="--stile-grey-8"
      headingContent={heading}
      textColor="--stile-grey-8"
      className={`${!subheading ? 'no-subheading' : ''} ${className || ''}`}
      {...filteredProps}
    >
      {subheading && <div className="--subheader">{subheading}</div>}
      <div className={`showcase-content ${stackVertical && !isMobile ? 'vertical-stack' : ''}`}>
        {stackVertical && !isMobile ? (
          <VerticalSlider {...getSliderProps()} />
        ) : (
          <RealWorldSlider {...getSliderProps()} />
        )}
      </div>
    </StyledSection>
  );
}
